import { Dialog as DialogPrimitive } from "radix-ui";
import React from "react";

import { Button } from "~/components/ui/button";
import { cn } from "~/components/ui/cn";
import { CloseIcon } from "~/components/ui/icons/close";

import "./modal.css";

const ModalContext = React.createContext({
  modal: false,
});

export const Modal = ({
  modal = false,
  ...props
}: DialogPrimitive.DialogProps) => {
  return (
    <ModalContext.Provider
      value={React.useMemo(() => ({ modal: modal }), [modal])}
    >
      <DialogPrimitive.Root {...props} modal={modal} />
    </ModalContext.Provider>
  );
};

export const ModalTrigger = DialogPrimitive.Trigger;

export const ModalClose = DialogPrimitive.Close;

export const ModalPortal = DialogPrimitive.Portal;

export const ModalOverlay: React.FC<
  React.ComponentProps<typeof DialogPrimitive.Overlay> & {
    className?: string;
  }
> = ({ className, ref, ...props }) => {
  const context = React.useContext(ModalContext);
  return context.modal ? (
    <DialogPrimitive.Overlay
      ref={ref}
      className={`fixed inset-0 z-40 bg-black/30 data-[state=closed]:duration-300 data-[state=open]:duration-300 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 ${className ?? ""}`}
      {...props}
    />
  ) : (
    <ModalTrigger asChild>
      <div
        className={`fixed inset-0 z-40 bg-black/30 data-[state=closed]:duration-300 data-[state=open]:duration-300 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 ${className ?? ""}`}
        data-state="open"
        ref={ref}
      ></div>
    </ModalTrigger>
  );
};

ModalOverlay.displayName = DialogPrimitive.Overlay.displayName;

export type ModalProps = {
  /**
   * Should the close button be hidden
   */
  hideCloseButton?: boolean;
  /**
   * Is the modal scrollable?
   */
  scrollable?: boolean;
};

export const ModalContent: React.FC<
  React.ComponentProps<typeof DialogPrimitive.Content> & ModalProps
> = ({
  children,
  className,
  hideCloseButton,
  scrollable,
  onFocusOutside,
  onPointerDownOutside,
  ...props
}) => {
  const context = React.useContext(ModalContext);
  return (
    <ModalPortal>
      <ModalOverlay />
      <DialogPrimitive.Content
        data-no-modal={!context.modal}
        className={cn(
          `modal-content fixed left-[50%] top-[50%] z-40 mx-auto grid w-full max-w-2.5xl translate-x-[-50%] translate-y-[-50%] gap-4 rounded-lg bg-white data-[state=closed]:duration-200 data-[state=open]:duration-300 data-[state=closed]:ease-out-cubic data-[state=open]:ease-in-cubic data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]`,
          scrollable ? "h-[calc(100vh_-_120px)] max-h-[37.5rem]" : "",
          className,
        )}
        {...props}
        onPointerDownOutside={
          onPointerDownOutside ??
          (!context.modal ? (e) => e.preventDefault() : undefined)
        }
        onFocusOutside={
          onFocusOutside ??
          (!context.modal ? (e) => e.preventDefault() : undefined)
        }
      >
        {children}
        {!hideCloseButton && (
          <ModalClose
            className="absolute bottom-[calc(100%+0.5rem)] right-0"
            asChild
          >
            <Button type="button" size="icon">
              <CloseIcon className="size-6 text-white" />
            </Button>
          </ModalClose>
        )}
      </DialogPrimitive.Content>
    </ModalPortal>
  );
};

ModalContent.displayName = DialogPrimitive.Content.displayName;

export const ModalTitle: React.FC<
  React.ComponentProps<typeof DialogPrimitive.Title>
> = ({ className, ...props }) => (
  <DialogPrimitive.Title
    className={cn("text-2xl font-bold text-gray-900", className)}
    {...props}
  />
);

ModalTitle.displayName = DialogPrimitive.Title.displayName;

export const ModalDescription: React.FC<
  React.ComponentProps<typeof DialogPrimitive.Description>
> = ({ ...props }) => <DialogPrimitive.Description {...props} />;

ModalDescription.displayName = DialogPrimitive.Description.displayName;

export const ModalHeader: React.FC<React.ComponentProps<"div">> = ({
  className,
  ...props
}) => <div className={cn("mb-4", className)} {...props} />;

ModalHeader.displayName = "ModalHeader";

export const ModalFooter: React.FC<React.ComponentProps<"div">> = ({
  className,
  ...props
}) => (
  <div
    className={cn(
      "flex flex-shrink-0 justify-end space-x-6 border-t border-gray-100 p-6 md:p-10 md:pt-6",
      className,
    )}
    {...props}
  />
);

ModalFooter.displayName = "ModalFooter";
